import { useState } from 'react';
import { useAuth } from '../../context';
import { Input, Button } from '../common';
import { useToastContext } from '../common/Toast';
import { hashPasswordWithSalt } from '../../utils/crypto';
import './auth-common.css';

const RegisterForm = ({ onSuccess }) => {
  const { register, loading } = useAuth();
  const { showToast } = useToastContext();
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });
  
  const [formErrors, setFormErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    
    if (!formData.username.trim()) {
      errors.username = '이메일을 입력해주세요';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.username)) {
      errors.username = '올바른 이메일 형식을 입력해주세요';
    }
    
    if (!formData.password) {
      errors.password = '비밀번호를 입력해주세요';
    } else if (formData.password.length < 8) {
      errors.password = '비밀번호는 최소 8자 이상이어야 합니다';
    }
    
    if (!formData.confirmPassword) {
      errors.confirmPassword = '비밀번호 확인을 입력해주세요';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = '비밀번호가 일치하지 않습니다';
    }
    
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const getErrorMessage = (error) => {
    if (!error) return '회원가입 중 오류가 발생했습니다.';
    
    if (error.includes('USER_EXISTS') || error.includes('already exists')) {
      return '이미 존재하는 이메일입니다. 다른 이메일을 사용해주세요.';
    }
    
    if (error.includes('COGNITO_ERROR') || error.includes('Failed to create user in Cognito')) {
      return '계정 생성 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    if (error.includes('DATABASE_ERROR') || error.includes('Failed to save user metadata')) {
      return '사용자 정보 저장 중 오류가 발생했습니다. 다시 시도해주세요.';
    }
    
    if (error.includes('INTERNAL_ERROR') || error.includes('Internal server error')) {
      return '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    if (error.includes('Network Error') || error.includes('ECONNABORTED')) {
      return '네트워크 연결을 확인하고 다시 시도해주세요.';
    }
    
    return error || '회원가입 중 오류가 발생했습니다.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setFormErrors({});
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    try {
      const { confirmPassword, ...registrationData } = formData;
      // 비밀번호 해시화
      const hashedPassword = hashPasswordWithSalt(registrationData.password, registrationData.username);
      const dataToSend = {
        ...registrationData,
        password: hashedPassword
      };
      
      const result = await register(dataToSend);
      
      if (result && result.success) {
        showToast({
          type: 'success',
          title: '회원가입 성공',
          message: '관리자 승인 후 로그인이 가능합니다.',
          duration: 5000
        });
        setFormData({
          username: '',
          password: '',
          confirmPassword: ''
        });
        
        if (onSuccess) {
          setTimeout(() => onSuccess(result), 1500);
        }
      } else {
        showToast({
          type: 'error',
          title: '회원가입 실패',
          message: getErrorMessage(result.message || result.error)
        });
      }
    } catch (err) {
      console.error('Registration error:', err);
      showToast({
        type: 'error',
        title: '회원가입 오류',
        message: getErrorMessage(err.message)
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      <Input
        label="이메일"
        type="email"
        name="username"
        value={formData.username}
        onChange={handleInputChange}
        error={formErrors.username}
        placeholder="이메일을 입력하세요"
        disabled={loading}
        required
      />
      
      <Input
        label="비밀번호"
        type="password"
        name="password"
        value={formData.password}
        onChange={handleInputChange}
        error={formErrors.password}
        placeholder="비밀번호를 입력하세요 (최소 8자)"
        hint="최소 8자 이상 입력해주세요"
        disabled={loading}
        required
      />
      
      <Input
        label="비밀번호 확인"
        type="password"
        name="confirmPassword"
        value={formData.confirmPassword}
        onChange={handleInputChange}
        error={formErrors.confirmPassword}
        placeholder="비밀번호를 다시 입력하세요"
        disabled={loading}
        required
      />
      
      <Button
        type="submit"
        variant="primary"
        size="md"
        loading={loading}
        disabled={loading}
        className="auth-form__submit"
      >
        회원가입
      </Button>
      
      <p className="auth-form__note">
        회원가입 후 관리자 승인을 기다려주세요.
      </p>
    </form>
  );
};

export default RegisterForm;